import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge, Banner, Button, Card, Page, Spinner } from "../../components";
import { describeApiError } from "../../api/errors";
import { getMfaStatus, regenerateRecoveryCodes } from "../../api/mfa";
import { useSession } from "../../api/session";
import { formatDateTime } from "../../api/types";
import { t } from "../../i18n/t";
import { RecoveryCodesPanel } from "../auth/RecoveryCodesPanel";

/**
 * xmg-sec-mfa (S3a): `/account/security`, a signed-in user's own two-step authentication settings - whether it is
 * on (always, once signed in), how this session passed it, how many recovery codes are left, and the one
 * self-service action: replace the recovery codes (asks for a fresh code first; the old set stops working). A lost
 * authenticator is recovered with a recovery code; a lost authenticator AND codes needs an operator reset.
 */
export function SecurityScreen(): JSX.Element {
  const { session } = useSession();
  const status = useQuery({ queryKey: ["mfa-status"], queryFn: getMfaStatus });
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onRegenerate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const issued = await regenerateRecoveryCodes();
      setCodes(issued.recovery_codes);
      void status.refetch();
    } catch (caught) {
      setError(describeApiError(caught, t("error.unexpected")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title={t("mfa.security.title")} caption={t("mfa.security.caption")}>
      {status.isPending ? <Spinner /> : null}
      {status.isError ? (
        <Banner tone="block">{describeApiError(status.error, t("error.unexpected"))}</Banner>
      ) : null}
      {status.data !== undefined ? (
        <Card title={t("mfa.security.card_title")} aside={<Badge tone="pass">{t("mfa.security.on")}</Badge>}>
          <dl className="mfastatus">
            <dt>{t("mfa.security.method")}</dt>
            <dd>{t("mfa.security.method_totp")}</dd>
            <dt>{t("mfa.security.this_session")}</dt>
            <dd>
              {status.data.method === "recovery_code"
                ? t("mfa.security.session_recovery")
                : t("mfa.security.session_totp")}
            </dd>
            <dt>{t("mfa.security.codes_left")}</dt>
            <dd>{status.data.recovery_codes_remaining}</dd>
            {status.data.step_up_valid_until ? (
              <>
                <dt>{t("mfa.security.step_up_until")}</dt>
                <dd>{formatDateTime(status.data.step_up_valid_until)}</dd>
              </>
            ) : null}
          </dl>
          {status.data.recovery_codes_remaining <= 3 && codes === null ? (
            <Banner tone="warn">{t("mfa.security.codes_low")}</Banner>
          ) : null}
          {codes === null ? (
            <>
              <p>{t("mfa.security.regenerate_body")}</p>
              <Button onClick={() => void onRegenerate()} disabled={busy}>
                {t("mfa.security.regenerate")}
              </Button>
              {error !== null ? <Banner tone="block">{error}</Banner> : null}
            </>
          ) : (
            <RecoveryCodesPanel
              codes={codes}
              account={session?.operator ?? ""}
              continueLabel={t("mfa.security.done")}
              onContinue={() => setCodes(null)}
            />
          )}
          <p className="muted">{t("mfa.security.lost_both")}</p>
        </Card>
      ) : null}
    </Page>
  );
}
